import { logger } from '../logger.js';
import { rememberServerDate } from './server-clock.js';

export interface RuntimeMaintenance {
  enabled: boolean;
  title: string;
  message: string;
  eta: string;
  channelUrl: string;
  allowAdmins: boolean;
}

/**
 * Where the home page's "Missing a feature? Tell us here" banner points when the
 * server does not supply a URL — /v1/config is unreachable, or the server predates
 * the `feedbackUrl` field. The live value comes from cti-admin-service (the
 * `feedbackUrl` platform setting, else its WEB_FEEDBACK_URL default), so operators
 * repoint the banner without rebuilding this bundle.
 */
export const DEFAULT_FEEDBACK_URL =
  'https://teams.microsoft.com/l/channel/19%3A4e8b8dba8be847918ec634c93253d78f%40thread.tacv2/Feature%20Requests?groupId=6479f24e-c5dd-487e-b2df-20c91b43507c&tenantId=ccd39903-04e9-47e2-9921-cc0693d548a4';

/**
 * The bug-report widget's channel when the server supplies none. A separate Teams
 * channel from the feedback one, so a bug report never lands in Feature Requests
 * just because this bundle could not reach /v1/config.
 */
export const DEFAULT_BUG_REPORT_URL =
  'https://teams.microsoft.com/l/channel/19%3A49beb5a8de234285877c665e59d760d3%40thread.tacv2/Bug%20Reports?groupId=6479f24e-c5dd-487e-b2df-20c91b43507c&tenantId=ccd39903-04e9-47e2-9921-cc0693d548a4';

/**
 * Where the employee guide lives when the server supplies no base. The live value comes from
 * cti-admin-service (`WEB_GUIDE_BASE_URL`), so an environment can point the in-product help
 * links at another copy of the site — a local preview of an article that is written but not
 * published yet — without rebuilding this bundle.
 */
export const DEFAULT_GUIDE_BASE_URL = 'https://cti.customertimes.com';

/**
 * The web SPA's runtime config, served by cti-admin-service at the public
 * GET /v1/config and fetched once at boot. Replaces build-time Vite env and the
 * scattered per-service gating: SSO/admin-frame identifiers, left-menu section
 * visibility, connector availability, and the feedback URL all arrive here from
 * one backend source.
 */
export interface RuntimeConfig {
  maintenance: RuntimeMaintenance;
  /**
   * Browser sign-in. `apiScope` is the full scope of the sign-in app's own API
   * (`api://{web host}/{client id}/access_as_user`). When set, the browser also asks
   * Entra for an access token for it and hands that to identity beside the ID token;
   * empty means it asks for nothing extra.
   */
  sso: {
    msalClientId: string;
    msalAuthority: string;
    apiScope: string;
    msalRedirectUri?: string;
  };
  adminFrame: { url: string; origin: string };
  features: {
    dashboards: boolean;
    diagrams: boolean;
    quality: boolean;
    insights: boolean;
    automations: boolean;
    decks: boolean;
    releaseNotes: boolean;
    skills: boolean;
    memory: boolean;
    /** "My Claude subscription" in the Personal workspace's Agent tab. */
    personalSubscription: boolean;
    /**
     * The Code Quality tab and its developer instruction. An opt-in feature that fails closed: it is
     * on only when the server sends exactly `true`, so an unreleased product never shows up because
     * /v1/config was down, old or malformed.
     */
    codeQuality: boolean;
    /** Workspace start-page editor and published Home layouts, enabled only by explicit true. */
    workspaceStartPages: boolean;
    /** The Tasks page and its tracker settings, on only for an explicit true and off after a failed fetch. */
    taskTracker: boolean;
  };
  /** Power BI is available only for an explicit true; other connector flags fail open. */
  connectors: Record<string, boolean>;
  feedbackUrl: string;
  /**
   * Where the bug-report widget sends people. Served by the same /v1/config, and
   * resolved independently of `feedbackUrl` — they are separate Teams channels.
   */
  bugReportUrl: string;
  /**
   * Origin (optionally with a path prefix) the in-product guide links are built on. No
   * trailing slash — `guide-links.ts` appends the rest.
   */
  guideBaseUrl: string;
  /**
   * Who a denied person should ask for access — an address, a channel link, or
   * plain words, as the operator typed it (cti-admin-service, ACCESS_REQUEST_CONTACT).
   * Empty means no contact is configured: the access-denied screen then shows no
   * such line, and a server that predates the field reads the same way. There is
   * no built-in default, unlike the feedback URLs — a wrong address is worse than
   * none.
   */
  accessRequestContact: string;
}

/**
 * Fail-open defaults: the app boots, sections are visible, connectors available,
 * SSO disabled (sign-in falls back to the dev-grant email path). Used until
 * /v1/config loads and whenever the fetch fails, so a config blip never bricks
 * the SPA or hides the whole UI. Code Quality, workspace start pages, the task tracker and Power BI
 * stay hidden here.
 */
const FALLBACK: RuntimeConfig = {
  maintenance: {
    enabled: false,
    title: '',
    message: '',
    eta: '',
    channelUrl: '',
    allowAdmins: true,
  },
  sso: { msalClientId: '', msalAuthority: '', apiScope: '' },
  adminFrame: { url: '', origin: '' },
  features: {
    dashboards: true,
    diagrams: true,
    quality: true,
    insights: true,
    automations: true,
    decks: true,
    releaseNotes: true,
    skills: true,
    memory: true,
    personalSubscription: true,
    codeQuality: false,
    workspaceStartPages: false,
    taskTracker: false,
  },
  connectors: { powerbi: false },
  feedbackUrl: DEFAULT_FEEDBACK_URL,
  bugReportUrl: DEFAULT_BUG_REPORT_URL,
  guideBaseUrl: DEFAULT_GUIDE_BASE_URL,
  accessRequestContact: '',
};

const FETCH_TIMEOUT_MS = 5000;

let cached: RuntimeConfig | null = null;

/** Whether `/v1/config` has answered at least once in this page, as opposed to the fallback standing in. */
let fetchedFromServer = false;

/**
 * Whether the config in hand came from the server. The fallback says SSO is off,
 * so a caller deciding that a session cannot be renewed must not take a failed
 * fetch for that answer.
 */
export function runtimeConfigFetched(): boolean {
  return fetchedFromServer;
}

/** Treat anything that isn't a plain object as absent, so a malformed payload
 *  (null / array / scalar for a section) falls back to defaults instead of
 *  spreading garbage (e.g. a string spreading into indexed chars). */
function obj(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Coerce an object's values to booleans, fail-open: anything not explicitly
 *  `false` becomes `true`. Used for the boolean-valued feature/connector maps so a
 *  malformed payload (null / string / missing) can never fail a section closed. */
function bools(value: unknown): Record<string, boolean> {
  return Object.fromEntries(
    Object.entries(obj(value)).map(([key, v]) => [key, v !== false]),
  );
}

/** A string field, or '' if the payload value is the wrong type. */
function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** A boolean field, or the default if the payload value is the wrong type. */
function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Build the config from a partial server payload, coercing EVERY scalar to its
 * declared type (fail-open defaults). A malformed /v1/config — wrong types, nulls,
 * non-objects — can never inject a bad value into a typed field.
 * `codeQuality` is read last and strictly, so the fail-open feature map cannot
 * switch it on.
 */
function withDefaults(data: Partial<RuntimeConfig>): RuntimeConfig {
  const maintenance = obj(data.maintenance);
  const sso = obj(data.sso);
  const adminFrame = obj(data.adminFrame);
  const features = obj(data.features);
  const connectors = obj(data.connectors);
  return {
    maintenance: {
      enabled: bool(maintenance.enabled, false),
      title: str(maintenance.title),
      message: str(maintenance.message),
      eta: str(maintenance.eta),
      channelUrl: str(maintenance.channelUrl),
      allowAdmins: bool(maintenance.allowAdmins, true),
    },
    sso: {
      msalClientId: str(sso.msalClientId),
      msalAuthority: str(sso.msalAuthority),
      apiScope: str(sso.apiScope),
      ...(str(sso.msalRedirectUri)
        ? { msalRedirectUri: str(sso.msalRedirectUri) }
        : {}),
    },
    adminFrame: { url: str(adminFrame.url), origin: str(adminFrame.origin) },
    features: {
      ...FALLBACK.features,
      ...bools(features),
      codeQuality: features.codeQuality === true,
      workspaceStartPages: features.workspaceStartPages === true,
      taskTracker: features.taskTracker === true,
    },
    connectors: {
      ...bools(connectors),
      powerbi: connectors.powerbi === true,
    },
    feedbackUrl: str(data.feedbackUrl) || DEFAULT_FEEDBACK_URL,
    // Its own default, never `feedbackUrl`: a server that predates the field
    // would otherwise send bug reports to the Feature Requests channel.
    bugReportUrl: str(data.bugReportUrl) || DEFAULT_BUG_REPORT_URL,
    /* A trailing slash would make every link a double slash, and an operator will type one
       sooner or later. */
    guideBaseUrl:
      str(data.guideBaseUrl).replace(/\/+$/, '') || DEFAULT_GUIDE_BASE_URL,
    /** No fallback on purpose: an older server that omits the field, and an
     *  operator who set no contact, both mean "show no contact line". */
    accessRequestContact: str(data.accessRequestContact),
  };
}

/**
 * Fetch /v1/config once and cache it. Best-effort: on timeout or error it keeps
 * the last value (or the fail-open fallback) so boot never hangs or blanks.
 * Pass `force` to refetch (e.g. the maintenance splash's "Try again").
 */
export async function loadRuntimeConfig(force = false): Promise<RuntimeConfig> {
  if (cached !== null && !force) return cached;
  // AbortController + setTimeout (not AbortSignal.timeout, which isn't available
  // in every runtime — a missing global would throw before the fetch and pin the
  // app to the fallback config).
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const requestStartedAt = Date.now();
    const res = await fetch('/v1/config', {
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`/v1/config responded ${res.status}`);
    rememberServerDate(res.headers.get('date'), requestStartedAt);
    cached = withDefaults((await res.json()) as Partial<RuntimeConfig>);
    fetchedFromServer = true;
  } catch (err: unknown) {
    logger.warn('runtime-config: /v1/config fetch failed, using fallback', err);
    const previous = cached ?? FALLBACK;
    cached = {
      ...previous,
      features: {
        ...previous.features,
        workspaceStartPages: false,
        taskTracker: false,
      },
      connectors: { ...previous.connectors, powerbi: false },
    };
  } finally {
    clearTimeout(timer);
  }
  return cached;
}

/**
 * Synchronous accessor for code that runs after boot loaded the config (SSO,
 * admin frame, sidebar gates). Returns the fail-open fallback if not yet loaded.
 */
export function getRuntimeConfig(): RuntimeConfig {
  return cached ?? FALLBACK;
}

/** Test-only: clears the module cache. */
export function _resetRuntimeConfigForTests(): void {
  cached = null;
  fetchedFromServer = false;
}
